import {get, isEmpty, isNil, isNumber} from 'lodash'
import {formatRoundedNumber} from "helpers/financial"

const vowels = ['a', 'e', 'i', 'o', 'u']

export const toArray = (string) => {
  if (typeof string !== 'string') return []
  return [...string.trim()]
}

export const lpad = (n, width, z) => {
  z = z || '0'
  n = n + ''
  return n.length >= width ? n : new Array(width - n.length + 1).join(z) + n
}

// Joins the array with the given 'joinOn' string, except for the last entry,
// which joins on 'lastJoinOn'. For example:
// specialJoin(['John','James','Jack', ', ', ' or ']) -> 'John, James or Jack'
export const specialJoin = (array, joinOn, lastJoinOn) => {
  if (array.length === 1) return array[0]
  const copy = [...array]
  const last = copy.pop()
  return `${copy.join(joinOn)}${lastJoinOn}${last}`
}

export const singular = (target) => {
  const startLetter = target.charAt(0)
  const article = (vowels.indexOf(startLetter.toLowerCase()) === -1) ? 'a' : 'an'
  return `${article} ${target}`
}

export const plural = (target) => {
  const lastLetter = target.charAt(target.length - 1)
  const secondLastLetter = target.charAt(target.length - 2)
  const secondLastLetterIsVoul = vowels.indexOf(secondLastLetter.toLowerCase()) !== -1
  const lastLetterIsY = lastLetter.toLowerCase() === 'y'
  const lastLetterIsUppercase = lastLetter.toUpperCase() === lastLetter
  if (lastLetterIsY && secondLastLetterIsVoul === false) {
    return (lastLetterIsUppercase) ? target.slice(0, -1) + 'IES' : target.slice(0, -1) + 'ies'
  }
  return (lastLetterIsUppercase) ? target + 'S' : target + 's'
}

export const capitalize = (s) => {
  return s && s[0].toUpperCase() + s.slice(1)
}

export const formatNumber = (number, locale, precision = 0) => {
  let ret = parseFloat(number)
  if (isNaN(ret)) return null
  ret = formatRoundedNumber(ret, precision)
  ret = parseFloat(ret).toLocaleString(locale, {minimumFractionDigits: precision})
  return ret
}

export const removeWhiteSpace = (value) => value.replace(/ /g, '')

export const defaultIfBlank = (value, defaultValue = null) => {
  if (typeof value === 'string') {
    return value.trim().length > 0 ? value.trim() : defaultValue
  }
  return value
}

export const unCamelCase = (str) => {
  str = str.replace(/([a-z\xE0-\xFF])([A-Z\xC0\xDF])/g, '$1 $2')
  str = str.toLowerCase()
  return str
}

export const getTitleForTotalItems = (count, singular, plural) => {
  if (isNil(count)) {
    return `0 ${plural}`
  }

  if (count === 0)
    return `0 ${plural}`

  if (count === 1)
    return `1 ${singular}`

  return `${count} ${plural}`
}

export const getTitleForItems = (items = [], singular, plural) => {
  const totalItems = isNil(items) ? 0 : items.length
  return getTitleForTotalItems(totalItems, singular, plural)
}

export const maxLength = (value, maxLength, addEllipsis = true) => {
  return value && value.length > maxLength
      ? addEllipsis
          ? value.substring(0, maxLength - 3) + '...'
          : value.substring(0, maxLength)
      : value || ''
}

export const isKeyInListUnique = (key, value, data = []) => {
  return (data.length === 0)
      ? true
      : isNil(data.find(d => {
        const dataValue = get(d, key)
        return isNil(dataValue) || isNil(value)
            ? false
            : dataValue.toLowerCase() === value.toLowerCase()
      }))
}

export const getKeyErrorText = (key, value, isUnique) => {
  if (isNil(value)) return null
  return isUnique ? null : `${key} must be unique`
}

export const isStringMatching = (value, search) =>
    !isNil(value) &&
    !isNil(search) &&
    value.toLowerCase().includes(search.toLowerCase())

export const getSearchTerm = (search) => {
  return isNil(search) || isEmpty(search) || search.trim().length < 3
      ? ''
      : search
}

export const getNumberFromLetterSequence = (orderItemIndex) => {
  if (isNil(orderItemIndex) || !isNumber(orderItemIndex)) {
    return null
  } else {
    const id = (orderItemIndex - 1) % 26
    const subid = parseInt((orderItemIndex - 1) / 26)
    const letter = String.fromCharCode(65 + id)
    if (subid > 0) {
      return String.fromCharCode(65 + (subid - 1)) + letter
    } else {
      return letter
    }
  }
}

export const getSimilarityScore = (string1, string2) => {
  const longer = string1.length < string2.length ? string2 : string1
  const shorter = string1.length < string2.length ? string1 : string2

  return longer.length === 0
    ? 1.0
    : (longer.length - getDifferenceCost(longer, shorter)) / parseFloat(longer.length)
}

export const getDifferenceCost = (longerString, shorterString) => {
  longerString = longerString.toLowerCase()
  shorterString = shorterString.toLowerCase()

  const costs = []
  for (let longerPos = 0; longerPos <= longerString.length; longerPos++) {
    let lastValue = longerPos

    for (let shorterPos = 0; shorterPos <= shorterString.length; shorterPos++) {
      if (longerPos === 0) {
        costs[shorterPos] = shorterPos
      } else {
        if (shorterPos > 0) {
          let newValue = costs[shorterPos - 1]
          if (longerString.charAt(longerPos - 1) !== shorterString.charAt(shorterPos - 1))
            newValue = Math.min(Math.min(newValue, lastValue), costs[shorterPos]) + 1

          costs[shorterPos - 1] = lastValue
          lastValue = newValue
        }
      }
    }

    if (longerPos > 0) costs[shorterString.length] = lastValue
  }

  return costs[shorterString.length]
}

export const getLineNumberFromAbsolutePosition = (value, position) => {
  let remaining = position
  const lines = value.split('\n')
  for (let i = 0;i < lines.length;i++) {
    const line = lines[i]
    if (line.length >= remaining - 1) {
      return {
        line: i + 1,
        character: remaining,
      }
    }
    remaining -= (line.length + 1)
  }

  return {
    line: lines.length,
    character: 1,
  }
}

export const tryFormatJson = (value) => {
  try {
    return JSON.stringify(JSON.parse(value), null, 2)
  } catch (e) {
    return value
  }
}

export default {
  toArray,
  specialJoin,
  plural,
  singular,
  capitalize,
  formatNumber,
  removeWhiteSpace,
  maxLength,
  isStringMatching,
}
